import React, { useRef } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Stack,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SessionQuestionEntityFrontend } from '../../../api/sessionQuestionsApi';
import { PublicSessionFileType } from '../../../api/publicSessionApi';
import { UploadEntry } from '../hooks/usePublicShareUploads';
import { TranslateFn } from '../hooks/useSessionFormI18n';
import { inferPublicSessionFileType } from './inferPublicSessionFileType';

export interface FileUploadFieldProps {
    question: SessionQuestionEntityFrontend;
    /** Existing uploads on the worker keyed by file type — drives "already uploaded" UX. */
    uploads: UploadEntry[];
    onUpload: (file: File, fileType: PublicSessionFileType) => Promise<UploadEntry>;
    t: TranslateFn;
}

const FILE_TYPE_KEY: Record<PublicSessionFileType, string> = {
    CV: 'file_type_cv',
    VCA: 'file_type_vca',
    RIJBEWIJS: 'file_type_driver_license',
    REFERENCE: 'file_type_reference',
    ID_DOCUMENT: 'file_type_id_document',
    CERTIFICATION: 'file_type_certification',
    OTHER: 'file_type_other',
};

/**
 * Inline file-upload widget that replaces the old "not available" alert when
 * the form encounters a `FILE` answer type. The mapping from question to
 * `fileType` is heuristic: question key / data key inspection picks the most
 * likely category; falls back to `OTHER` so we never block the worker.
 */
export const FileUploadField: React.FC<FileUploadFieldProps> = ({
    question,
    uploads,
    onUpload,
    t,
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const fileType = inferPublicSessionFileType(question);

    const lastUploadForType = [...uploads]
        .reverse()
        .find((u) => u.fileType === fileType);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        await onUpload(file, fileType);
    };

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1">
                    {question.questionShorten || question.question}
                </Typography>
                <Chip size="small" label={t(FILE_TYPE_KEY[fileType])} variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button
                    variant="outlined"
                    onClick={() => inputRef.current?.click()}
                    disabled={lastUploadForType?.status === 'uploading'}
                >
                    {lastUploadForType?.status === 'uploading' ? (
                        <CircularProgress size={20} />
                    ) : lastUploadForType?.status === 'done' ? (
                        t('file_field_replace')
                    ) : (
                        t('file_field_upload')
                    )}
                </Button>
                {lastUploadForType?.status === 'done' && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                        <Typography variant="body2">{lastUploadForType.file.name}</Typography>
                    </Stack>
                )}
                {lastUploadForType?.status === 'error' && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ErrorOutlineIcon color="error" fontSize="small" />
                        <Typography variant="caption" color="error.main">
                            {lastUploadForType.error}
                        </Typography>
                    </Stack>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    onChange={handleChange}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.csv,.html,.json"
                />
            </Stack>
            {lastUploadForType?.status === 'uploading' && (
                <LinearProgress
                    variant="determinate"
                    value={lastUploadForType.progress}
                    sx={{ mt: 1 }}
                />
            )}
        </Box>
    );
};
